import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import {
  Button,
  LoadingPromiseButtonAdvanced,
  Text,
} from '@pflegenavi/web-components';
import { useFormatting } from '@pflegenavi/frontend/localization';

interface CancelReceiptBatchEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (note: string) => Promise<void>;
  entryAmount: number;
  residentName: string;
  isLateCancellation: boolean;
  availableBalance?: number;
}

export const CancelReceiptBatchEntryDialog: FC<
  CancelReceiptBatchEntryDialogProps
> = ({
  open,
  onClose,
  onConfirm,
  entryAmount,
  residentName,
  isLateCancellation,
  availableBalance,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const [note, setNote] = useState('');

  const handleConfirm = useCallback(async () => {
    await onConfirm(note);
    setNote('');
  }, [onConfirm, note]);

  const handleClose = useCallback(() => {
    setNote('');
    onClose();
  }, [onClose]);

  const isBalanceLoading = isLateCancellation && availableBalance === undefined;

  const insufficientFunds =
    isLateCancellation &&
    availableBalance !== undefined &&
    availableBalance < entryAmount;

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          width: '80%',
          maxHeight: 500,
        },
      }}
      maxWidth="xs"
      open={open}
      onClose={handleClose}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Text variant="h5" sx={{ fontWeight: 700 }}>
          {t('receipts.batch.entry.cancel-title')}
        </Text>
      </DialogTitle>
      <DialogContent>
        {isLateCancellation ? (
          <>
            <Typography sx={{ mb: 2 }}>
              {t('receipts.batch.entry.cancel-late-description', {
                amount: fCurrencyCents(entryAmount),
                availableBalance: isBalanceLoading
                  ? '—'
                  : fCurrencyCents(availableBalance ?? 0),
              })}
            </Typography>
            {insufficientFunds && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t('receipts.batch.entry.cancel-late-insufficient-funds')}
              </Alert>
            )}
          </>
        ) : (
          <Typography sx={{ mb: 2 }}>
            {t('receipts.batch.entry.cancel-regular-description', {
              residentName,
              amount: fCurrencyCents(entryAmount),
            })}
          </Typography>
        )}
        <TextField
          label={t('receipts.batch.entry.cancel-note-label')}
          placeholder={t('receipts.batch.entry.cancel-note-placeholder')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ pt: 0 }}>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <LoadingPromiseButtonAdvanced
          disabled={insufficientFunds || isBalanceLoading}
          variant="contained"
          color="error"
          onClick={handleConfirm}
          size="small"
        >
          {t('receipts.batch.entry.cancel-confirm')}
        </LoadingPromiseButtonAdvanced>
      </DialogActions>
    </Dialog>
  );
};
